import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';
import { Switch } from 'ksnm-common-ui/lib/Switch';
import { Table } from 'ksnm-common-ui/lib/Table2/Table';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { constantStore } from '@stores/constantStore';
import { dataControlStore } from '@stores/dataControlStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { columns } from './columns';
import { useUpdateTitle } from './hooks/useUpdateTitle';
import { LaunchesModal } from './LaunchesModal';
import { Title } from './Title';

import styles from '../DataControl.module.scss';

const { TABLE_NAMES } = constantStore;

export const PackageLevel = observer(() => {
  const [onlyCurrentLaunch, setOlyCurrentLaunch] = useState<boolean>(true);
  const [selectCtrlVers, setSelectCtrlVers] = useState<string | null>(null);

  const { lastPagesParam, filters } = dataControlStore;

  const [key, update] = useUpdateTable({ tableName: TABLE_NAMES.PACKAGE_LEVEL });

  const hardFilter = useMemo(() => {
    const out = [...(lastPagesParam.filters?.[0] ?? [])];

    const { controlName, status } = filters.packageLevel || {};

    if (onlyCurrentLaunch) {
      out.push({
        name: 'is_last_run',
        type: FILTER.EQ,
        values: [true],
        filterIsInverted: false,
      });
    }

    if (controlName) {
      out.push({
        name: 'ctrl_vers_ext',
        type: FILTER.EQ,
        values: [controlName],
        filterIsInverted: false,
      });
    }

    if (status) {
      out.push({
        name: 'status',
        type: FILTER.EQ,
        values: [status],
        filterIsInverted: false,
      });
    }

    update();

    return [out];
  }, [filters.packageLevel, lastPagesParam.filters, onlyCurrentLaunch, update]);

  const { titleData, updateTitle } = useUpdateTitle({ reportPackageCodeCm: lastPagesParam.reportPackageCodeCm ?? '' });

  const [title, onChangeTable] = useTableTitleWithCount(
    <>
      <Title data={titleData} />

      <div className={styles.switch}>
        <Switch
          checked={onlyCurrentLaunch}
          onChange={(v) => {
            setOlyCurrentLaunch(v);
            update();
          }}
        />
        Результаты только актуальных запусков
      </div>
    </>,
  );

  const onRefresh = useCallback(() => {
    update();
    updateTitle();
  }, [update, updateTitle]);

  const extendTableButtons = useMemo(
    () => (
      <>
        <IconButton key="refresh" iconName="refresh" title="Обновить" onClick={onRefresh} />
      </>
    ),
    [onRefresh],
  );

  const onRowClick = useCallback(({ ctrl_vers }) => ctrl_vers && setSelectCtrlVers(ctrl_vers), []);

  const rowClassName = useCallback((record) => (record.is_last_run ? '' : styles.bgRowYellow), []);

  return (
    <>
      <Table
        columns={columns}
        tableTitle={title}
        hardFilter={hardFilter}
        tableName={TABLE_NAMES.PACKAGE_LEVEL}
        resource="v_ctrl_pckg"
        externalFetch={ksnmTableStore.externalFetch}
        externalFetchXLSX={ksnmTableStore.getExcelStreamV2}
        onTableChange={onChangeTable}
        showAggregateRow={false}
        cellHeight={64}
        key={key}
        extendTableButtons={extendTableButtons}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
      />

      <LaunchesModal setSelectCtrlVers={setSelectCtrlVers} selectCtrlVers={selectCtrlVers} />
    </>
  );
});
